import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Receipt, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Billing card — viewer-facing.
 * Lists the logged-in user's paid Stripe transactions and lets them resend
 * a copy of any receipt to the email on file. Hides silently if the user has
 * no paid transactions (e.g. comped admin/staff accounts).
 */
function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function formatAmount(amount, currency) {
  const sym = (currency || "GBP").toUpperCase() === "GBP" ? "£" : `${currency} `;
  return `${sym}${Number(amount || 0).toFixed(2)}`;
}

export default function BillingCard() {
  const [receipts, setReceipts] = useState(null); // null = loading, [] = empty
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/receipts");
        setReceipts(r.data?.receipts || []);
      } catch {
        setReceipts([]);
      }
    })();
  }, []);

  const resend = async (sessionId) => {
    setBusyId(sessionId);
    try {
      const r = await api.post(`/me/receipts/${encodeURIComponent(sessionId)}/resend`);
      toast.success(`Receipt emailed to ${r.data?.sent_to || "your inbox"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't resend receipt. Try again shortly.");
    } finally {
      setBusyId(null);
    }
  };

  // Hide the whole card for users who don't have any paid transactions on file
  // (e.g. admins, comped accounts, support tickets etc.).
  if (receipts === null || receipts.length === 0) return null;

  const latest = receipts[0];

  return (
    <section
      id="billing"
      className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-8"
      data-testid="billing-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-11 w-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Receipt size={18} strokeWidth={1.7} />
          </div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Billing</div>
            <h3 className="mt-1 font-heading text-xl text-slate-900 font-medium tracking-tight">
              Your receipts
            </h3>
            <p className="mt-1 text-sm text-slate-500 leading-relaxed max-w-md">
              Download your Stripe invoice PDF anytime, or ask us to email you another copy of the receipt.
            </p>
          </div>
        </div>

        <Button
          onClick={() => resend(latest.session_id)}
          disabled={busyId === latest.session_id}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5"
          data-testid="billing-resend-latest"
        >
          <Mail size={15} className="mr-2" strokeWidth={1.8} />
          {busyId === latest.session_id ? "Sending…" : "Resend latest receipt"}
        </Button>
      </div>

      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400 font-semibold mb-3">
          All paid transactions
        </div>
        <ul className="divide-y divide-slate-100" data-testid="billing-receipts-list">
          {receipts.map((r) => (
            <li
              key={r.session_id}
              className="py-3 flex items-center justify-between gap-4 flex-wrap"
              data-testid={`billing-receipt-${r.session_id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900">
                  {formatAmount(r.amount_gbp, r.currency)}
                  {r.tier ? (
                    <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5">
                      {r.tier}
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  Paid {formatDate(r.paid_at)} · ref {String(r.session_id).slice(-10)}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                {(r.stripe_invoice_pdf || r.stripe_invoice_url) && (
                  <a
                    href={r.stripe_invoice_pdf || r.stripe_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center h-9 px-4 rounded-full text-xs font-semibold text-[#0F8FB0] bg-[#28BBE3]/10 hover:bg-[#28BBE3]/20 ring-1 ring-[#28BBE3]/30"
                    data-testid={`billing-invoice-${r.session_id}`}
                  >
                    <ExternalLink size={13} className="mr-1.5" strokeWidth={1.8} />
                    Download invoice
                  </a>
                )}
                <Button
                  variant="outline"
                  onClick={() => resend(r.session_id)}
                  disabled={busyId === r.session_id}
                  className="rounded-full text-xs border-slate-200 text-slate-700 hover:bg-slate-50 h-9 px-4"
                  data-testid={`billing-resend-${r.session_id}`}
                >
                  <Mail size={13} className="mr-1.5" strokeWidth={1.8} />
                  {busyId === r.session_id ? "Sending…" : "Resend"}
                </Button>
              </div>
            </li>
          ))}
        </ul>

        <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
          For a VAT-style invoice or a name/address change, email{" "}
          <a href="mailto:hello@dontforgetthebubbles.com" className="underline hover:text-slate-600">
            hello@dontforgetthebubbles.com
          </a>
          .
        </p>
      </div>
    </section>
  );
}
